import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, InputGroup, Modal, Row, Spinner } from 'react-bootstrap';
import { settingsApi, visitApi } from '../../api';
import { useToast } from '../../context/ToastContext';
import { formatAge, formatMoney, titleCase } from '../../utils/format';

const EMPTY_VITALS = {
  weight: '',
  bpSystolic: '',
  bpDiastolic: '',
  pulse: '',
  temperature: '',
  spo2: '',
  sugar: '',
};

/**
 * Reception's "send the patient in" step: today's vitals, the reason for the
 * visit and the fee. Saving puts the patient on the doctor's queue with a token.
 */
export default function StartVisitModal({ show, patient, onHide, onDone }) {
  const toast = useToast();
  const [vitals, setVitals] = useState(EMPTY_VITALS);
  const [reason, setReason] = useState('');
  const [fee, setFee] = useState('');
  const [paid, setPaid] = useState('');
  const [mode, setMode] = useState('cash');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !patient) return;

    setVitals({ ...EMPTY_VITALS, weight: patient.weight ?? '' });
    setReason('');
    setError('');

    // Pre-fill the fee the clinic has configured, so the desk just confirms it.
    settingsApi
      .get()
      .then((res) => {
        const isRevisit = (patient.totalVisits || 0) > 0;
        const amount = isRevisit ? res.data.followUpFee : res.data.defaultConsultationFee;
        setFee(String(amount ?? 0));
        setPaid(String(amount ?? 0));
      })
      .catch(() => {
        setFee('0');
        setPaid('0');
      });
  }, [show, patient]);

  function setVital(field, value) {
    setVitals((v) => ({ ...v, [field]: value }));
  }

  function numeric(value) {
    return value === '' ? undefined : Number(value);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await visitApi.create({
        patient: patient._id,
        reasonForVisit: reason.trim(),
        vitals: Object.fromEntries(
          Object.entries(vitals)
            .map(([k, v]) => [k, numeric(v)])
            .filter(([, v]) => v !== undefined),
        ),
        fees: { consultation: numeric(fee) || 0, paid: numeric(paid) || 0, mode },
      });

      toast.success(`${patient.name} added to the queue on token ${res.data.tokenNo}`);
      onDone?.(res.data);
      onHide();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (!patient) return null;

  const isRevisit = (patient.totalVisits || 0) > 0;
  const balance = Math.max(0, Number(fee || 0) - Number(paid || 0));

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            <i className="bi bi-clipboard2-plus me-2" style={{ color: 'var(--clinic-primary)' }} />
            Send to Doctor
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="bg-light rounded p-3 mb-3 d-flex flex-wrap justify-content-between gap-2">
            <div>
              <div className="fw-bold fs-5">{patient.name}</div>
              <div className="text-muted small">
                {patient.patientId} &middot; {titleCase(patient.gender)}, {formatAge(patient)} &middot;{' '}
                <i className="bi bi-telephone" /> {patient.mobile}
              </div>
            </div>
            <div className="text-end">
              <span className={`badge ${isRevisit ? 'bg-info' : 'bg-success'}`}>
                {isRevisit ? `Revisit (${patient.totalVisits} before)` : 'First visit'}
              </span>
            </div>
          </div>

          {patient.allergies?.length > 0 && (
            <div className="allergy-alert mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2" />
              Allergic to: <strong>{patient.allergies.join(', ')}</strong>
            </div>
          )}

          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>Reason for visit / what the patient says</Form.Label>
            <Form.Control
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Fever and cough since 3 days"
            />
          </Form.Group>

          <div className="fw-semibold small text-uppercase text-muted mb-2">
            Today's Vitals
            <span className="text-muted fw-normal text-lowercase ms-2">leave blank what you did not measure</span>
          </div>
          <Row className="g-2 mb-3">
            <Col xs={6} md={3}>
              <Form.Label>Weight</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={vitals.weight}
                  onChange={(e) => setVital('weight', e.target.value)}
                />
                <InputGroup.Text>kg</InputGroup.Text>
              </InputGroup>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label>Blood Pressure</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  placeholder="120"
                  value={vitals.bpSystolic}
                  onChange={(e) => setVital('bpSystolic', e.target.value)}
                />
                <InputGroup.Text className="px-2">/</InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="80"
                  value={vitals.bpDiastolic}
                  onChange={(e) => setVital('bpDiastolic', e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label>Pulse</Form.Label>
              <InputGroup>
                <Form.Control type="number" value={vitals.pulse} onChange={(e) => setVital('pulse', e.target.value)} />
                <InputGroup.Text>/min</InputGroup.Text>
              </InputGroup>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label>Temperature</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={vitals.temperature}
                  onChange={(e) => setVital('temperature', e.target.value)}
                />
                <InputGroup.Text>F</InputGroup.Text>
              </InputGroup>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label>SpO2</Form.Label>
              <InputGroup>
                <Form.Control type="number" value={vitals.spo2} onChange={(e) => setVital('spo2', e.target.value)} />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Col>
          </Row>

          <div className="fw-semibold small text-uppercase text-muted mb-2">Fees</div>
          <Row className="g-2 align-items-end">
            <Col xs={6} md={3}>
              <Form.Label>Consultation</Form.Label>
              <InputGroup>
                <InputGroup.Text>&#8377;</InputGroup.Text>
                <Form.Control type="number" value={fee} onChange={(e) => setFee(e.target.value)} />
              </InputGroup>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label>Received Now</Form.Label>
              <InputGroup>
                <InputGroup.Text>&#8377;</InputGroup.Text>
                <Form.Control type="number" value={paid} onChange={(e) => setPaid(e.target.value)} />
              </InputGroup>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label>Mode</Form.Label>
              <Form.Select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="other">Other</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <div className="bg-light rounded px-3 py-2 text-center">
                <div className="vital-label">Balance</div>
                <strong className={balance > 0 ? 'text-danger' : 'text-success'}>{formatMoney(balance)}</strong>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <span className="text-muted small d-none d-sm-inline">
            <i className="bi bi-info-circle me-1" />
            The doctor sees this patient on their queue straight away.
          </span>
          <div className="d-flex gap-2">
            <Button variant="light" onClick={onHide} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" size="lg" className="btn-clinic px-4" disabled={busy}>
              {busy ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-send me-2" />}
              Add to Queue
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
